import { detailsSlots } from '@/shared/details'
import { shellSlots, type Feature } from '@/shared/registry'
import { FileText, Globe } from '@/shared/ui/icons'

import { actingSiteSlots } from './acting-site'
import { adminHomeLayout } from './admin-home'
import { companyAdminHomeLinks, companyRoutes } from './companies'
import { userCustomFieldAdminHomeLinks, userCustomFieldRoutes, userCustomFieldSlots } from './custom-fields'
import { dataDownloadRoutes } from './data-download'
import { instructorAdminHomeLinks, instructorRoutes, instructorSlots } from './instructors'
import {
  createLicenseAgreementPath,
  editLicenseAgreementRoute,
  licenseAgreementRoute,
  licenseAgreementsPath,
  readLicenseAgreementsPermission,
  writeLicenseAgreementsPermission,
} from './license-agreements/paths'
import { myProfileRoutes, myProfileSlots } from './me'
import {
  createOrganizationPath,
  createOrganizationsPermission,
  editOrganizationRoute,
  organizationBasicFieldsSlot,
  organizationRoute,
  organizationsPath,
  organizationTreePath,
  readOrganizationsPermission,
  readOrganizationTreePermission,
} from './organizations/paths'
import { SiteManagementProvider } from './SiteManagementProvider'
import { savedSearchSlots } from './saved-searches'
import { userGroupAdminHomeLinks, userGroupRoutes, userGroupSlots } from './user-groups'
import { userBulkEditRoutes } from './users/bulk-edits'
import { userImportAdminHomeLinks, userImportRoutes } from './users/imports'
import {
  createUserPath,
  createUserScopes,
  editUserRoute,
  readSignInHistoryPermission,
  readUsersPermission,
  userRoute,
  usersPath,
  writeUsersPermission,
} from './users/paths'
import { signInsPath, userSignInsRoute } from './users/sign-in'
import { userDetailsSlot } from './users/user'
import { createSitePath, createSiteScope, editSiteRoute, readSitesPermission, siteRoute, sitesPath, writeSitesPermission } from './sites/paths'
import {
  createWebPagePath,
  editWebPageRoute,
  publishedWebPageRoute,
  readWebPagesPermission,
  webPageRoute,
  webPagesPath,
  writeWebPagesPermission,
} from './web-pages/paths'

/** Site Management: sites, organizations, users, web pages and the site of each request. Its screens register here. */
export const feature: Feature = {
  id: 'site-management',
  // Loads the current site for the whole app, signed in or not, and names users on "Created by" lines.
  provider: SiteManagementProvider,
  routes: [
    ...companyRoutes,
    ...userGroupRoutes,
    ...myProfileRoutes,
    ...instructorRoutes,
    ...userCustomFieldRoutes,
    ...userImportRoutes,
    ...userBulkEditRoutes,
    ...dataDownloadRoutes,
    {
      path: organizationsPath,
      titleId: 'siteManagement.organizations.find.title',
      page: () => import('./organizations/FindOrganizationsPage').then((module) => module.FindOrganizationsPage),
    },
    {
      path: createOrganizationPath,
      titleId: 'siteManagement.organizations.create.title',
      page: () => import('./organizations/OrganizationFormPages').then((module) => module.CreateOrganizationPage),
    },
    {
      path: organizationTreePath,
      titleId: 'siteManagement.organizations.tree.title',
      page: () => import('./organizations/OrganizationTreePage').then((module) => module.OrganizationTreePage),
    },
    {
      path: organizationRoute,
      titleId: 'siteManagement.organizations.details.title',
      page: () => import('./organizations/OrganizationDetailsPage').then((module) => module.OrganizationDetailsPage),
    },
    {
      path: editOrganizationRoute,
      titleId: 'siteManagement.organizations.edit.title',
      page: () => import('./organizations/OrganizationFormPages').then((module) => module.EditOrganizationPage),
    },
    {
      path: licenseAgreementsPath,
      titleId: 'siteManagement.licenseAgreements.find.title',
      page: () => import('./license-agreements/FindLicenseAgreementsPage').then((module) => module.FindLicenseAgreementsPage),
    },
    {
      path: createLicenseAgreementPath,
      titleId: 'siteManagement.licenseAgreements.create.title',
      page: () => import('./license-agreements/LicenseAgreementFormPages').then((module) => module.CreateLicenseAgreementPage),
    },
    {
      path: licenseAgreementRoute,
      titleId: 'siteManagement.licenseAgreements.details.title',
      page: () => import('./license-agreements/LicenseAgreementDetailsPage').then((module) => module.LicenseAgreementDetailsPage),
    },
    {
      path: editLicenseAgreementRoute,
      titleId: 'siteManagement.licenseAgreements.edit.title',
      page: () => import('./license-agreements/LicenseAgreementFormPages').then((module) => module.EditLicenseAgreementPage),
    },
    {
      path: usersPath,
      titleId: 'siteManagement.users.find.title',
      page: () => import('./users/FindUsersPage').then((module) => module.FindUsersPage),
    },
    {
      path: createUserPath,
      titleId: 'siteManagement.users.create.title',
      page: () => import('./users/UserFormPages').then((module) => module.CreateUserPage),
    },
    {
      path: userRoute,
      titleId: 'siteManagement.users.details.title',
      page: () => import('./users/UserDetailsPage').then((module) => module.UserDetailsPage),
    },
    {
      path: editUserRoute,
      titleId: 'siteManagement.users.edit.title',
      page: () => import('./users/UserFormPages').then((module) => module.EditUserPage),
    },
    {
      path: signInsPath,
      titleId: 'siteManagement.signIns.find.title',
      page: () => import('./users/FindSignInsPage').then((module) => module.FindSignInsPage),
    },
    {
      path: userSignInsRoute,
      titleId: 'siteManagement.signIns.history.title',
      page: () => import('./users/FindSignInsPage').then((module) => module.UserSignInsPage),
    },
    {
      path: sitesPath,
      titleId: 'siteManagement.sites.find.title',
      page: () => import('./sites/FindSitesPage').then((module) => module.FindSitesPage),
    },
    {
      path: createSitePath,
      titleId: 'siteManagement.sites.create.title',
      page: () => import('./sites/SiteFormPages').then((module) => module.CreateSitePage),
    },
    {
      path: siteRoute,
      titleId: 'siteManagement.sites.details.title',
      page: () => import('./sites/SiteDetailsPage').then((module) => module.SiteDetailsPage),
    },
    {
      path: editSiteRoute,
      titleId: 'siteManagement.sites.edit.title',
      page: () => import('./sites/SiteFormPages').then((module) => module.EditSitePage),
    },
    {
      path: webPagesPath,
      titleId: 'siteManagement.webPages.find.title',
      page: () => import('./web-pages/FindWebPagesPage').then((module) => module.FindWebPagesPage),
    },
    {
      path: createWebPagePath,
      titleId: 'siteManagement.webPages.create.title',
      page: () => import('./web-pages/WebPageFormPages').then((module) => module.CreateWebPagePage),
    },
    {
      path: webPageRoute,
      titleId: 'siteManagement.webPages.details.title',
      page: () => import('./web-pages/WebPageDetailsPage').then((module) => module.WebPageDetailsPage),
    },
    {
      path: editWebPageRoute,
      titleId: 'siteManagement.webPages.edit.title',
      page: () => import('./web-pages/WebPageFormPages').then((module) => module.EditWebPagePage),
    },
    {
      // Public: an anonymous page opens signed out; any other sends the person to sign in first.
      path: publishedWebPageRoute,
      titleId: 'siteManagement.webPages.published.title',
      page: () => import('./web-pages/PublishedWebPage').then((module) => module.WebPageByIdPage),
      public: true,
    },
    {
      // The catch-all: tries the address as a vanity URL before Not Found, signing in first when signed out.
      path: '*',
      titleId: 'siteManagement.webPages.published.title',
      page: () => import('./web-pages/PublishedWebPage').then((module) => module.VanityUrlPage),
      public: true,
    },
  ],
  adminHome: {
    layout: adminHomeLayout,
    categories: [
      { id: 'sites', section: 'system', titleId: 'siteManagement.sites.adminHomeCategory', icon: Globe },
      { id: 'web-pages', section: 'system', titleId: 'siteManagement.webPages.adminHomeCategory', icon: FileText },
    ],
    links: [
      { id: 'find-organizations', category: 'organizations', labelId: 'siteManagement.organizations.find.title', path: organizationsPath, permission: readOrganizationsPermission },
      { id: 'manage-organization-tree', category: 'organizations', labelId: 'siteManagement.organizations.tree.adminLink', path: organizationTreePath, permission: readOrganizationTreePermission },
      { id: 'create-organization', category: 'organizations', labelId: 'siteManagement.organizations.create.title', path: createOrganizationPath, permission: createOrganizationsPermission },
      { id: 'find-license-agreements', category: 'license-agreements', labelId: 'siteManagement.licenseAgreements.find.title', path: licenseAgreementsPath, permission: readLicenseAgreementsPermission },
      { id: 'create-license-agreement', category: 'license-agreements', labelId: 'siteManagement.licenseAgreements.create.title', path: createLicenseAgreementPath, permission: writeLicenseAgreementsPermission },
      { id: 'find-users', category: 'users', labelId: 'siteManagement.users.find.title', path: usersPath, permission: readUsersPermission },
      { id: 'create-user', category: 'users', labelId: 'siteManagement.users.create.title', path: createUserPath, permission: writeUsersPermission, permissionScope: createUserScopes },
      { id: 'find-sign-ins', category: 'users', labelId: 'siteManagement.signIns.find.title', path: signInsPath, permission: readSignInHistoryPermission },
      ...companyAdminHomeLinks,
      ...userGroupAdminHomeLinks,
      ...userCustomFieldAdminHomeLinks,
      ...userImportAdminHomeLinks,
      ...instructorAdminHomeLinks,
      { id: 'find-sites', category: 'sites', labelId: 'siteManagement.sites.find.title', path: sitesPath, permission: readSitesPermission },
      { id: 'create-site', category: 'sites', labelId: 'siteManagement.sites.create.title', path: createSitePath, permission: writeSitesPermission, permissionScope: createSiteScope },
      { id: 'find-web-pages', category: 'web-pages', labelId: 'siteManagement.webPages.find.title', path: webPagesPath, permission: readWebPagesPermission },
      { id: 'create-web-page', category: 'web-pages', labelId: 'siteManagement.webPages.create.title', path: createWebPagePath, permission: writeWebPagesPermission },
    ],
  },
  slots: [
    ...actingSiteSlots,
    ...userGroupSlots,
    ...savedSearchSlots,
    ...myProfileSlots,
    ...instructorSlots,
    ...userCustomFieldSlots,
    {
      // The organization's own agreement, on the organization form's Basic Information.
      kind: 'panel',
      slot: organizationBasicFieldsSlot,
      id: 'license-agreement',
      component: () =>
        import('./license-agreements/OrganizationLicenseAgreementField').then((module) => module.OrganizationLicenseAgreementField),
    },
    {
      // Holds a person until they accept each organization agreement up their tree.
      kind: 'panel',
      slot: shellSlots.gates,
      id: 'organization-license-agreements',
      component: () =>
        import('./license-agreements/OrganizationAgreementGate').then((module) => module.OrganizationAgreementGate),
    },
    {
      // Notes about the person, on User Details.
      kind: 'tab',
      slot: detailsSlots(userDetailsSlot).tabs,
      id: 'notes',
      labelId: 'siteManagement.users.tab.notes',
      component: () => import('./users/notes/UserNotesTab').then((module) => module.UserNotesTab),
      count: () => import('./users/notes/UserNotesCount').then((module) => module.UserNotesCount),
    },
    {
      // Sign In History, on User Details.
      kind: 'tab',
      slot: detailsSlots(userDetailsSlot).tabs,
      id: 'security',
      labelId: 'siteManagement.users.tab.security',
      component: () => import('./users/SecurityAndHistoryTab').then((module) => module.SecurityAndHistoryTab),
    },
  ],
}
